import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Outlet } from "react-router";
import { toast } from "react-toastify";
import { PlaylistService } from "../api";
import type { SearchVideoResult } from "../api/generated/data-contracts";
import { logout } from "../redux/slices/auth";
import { upcomingSongsSelector } from "../redux/slices/songs";
import { useTypedSelector } from "../redux/store";
import { useGetVotedSongs } from "../hooks/useGetVotedSongs";

export interface PublicOutletContext {
  addItemToPlaylist: (songResult: SearchVideoResult) => Promise<void>;
  handleVote: (id: string, value: number) => Promise<void>;
  upcomingSongs: ReturnType<typeof upcomingSongsSelector>;
}

export const PlaylistCallbacksProvider: FC = () => {
  const [localVotedSongs, _setVotedSongs] = useState<Record<string, boolean>>(
    {}
  );

  const dispatch = useDispatch();
  const upcomingSongs = useTypedSelector(upcomingSongsSelector);

  const userJwt = useTypedSelector((state) => state.auth.value);

  const { data } = useGetVotedSongs();

  const votedSongs: Record<string, boolean> = useMemo(() => {
    const fromData =
      data?.results?.reduce<Record<string, boolean>>((acc, x) => {
        acc[x.videoId] = localVotedSongs[x.videoId] ?? true;
        return acc;
      }, {}) ?? {};

    return { ...fromData, ...localVotedSongs };
  }, [data, localVotedSongs]);

  const _upcomingSongs = useMemo(
    () =>
      upcomingSongs.map((song) => ({
        ...song,
        voted: votedSongs?.[song.id] ?? false
      })),
    [votedSongs, upcomingSongs]
  );

  const addItemToPlaylist = useCallback(
    async (songResult: SearchVideoResult): Promise<void> => {
      await PlaylistService.addvideoCreate(
        {
          youtubeId: songResult.youtubeId,
          title: songResult.title,
          thumbnail: songResult.thumbnail
        },
        {
          headers: {
            Authorization: `Bearer ${userJwt}`
          }
        }
      )
        .then((res) => {
          if (res.status === 200) {
            _setVotedSongs((prev) => ({
              ...prev,
              [res.data.id]: true
            }));

            toast.success("Successfully added!");
          } else {
            toast.error("Something went wrong, please try again.");
          }
        })
        .catch((res) => {
          if (res.response?.status === 400) {
            for (const property in res.response?.data.errors) {
              res.response?.data.errors[property].forEach((eMessage: string) =>
                toast.error(eMessage)
              );
            }
          } else if (res.response?.status === 401) {
            dispatch(logout());
          } else {
            toast.error("Something went wrong, please try again.");
          }
        });
    },
    [dispatch, userJwt]
  );

  const updateVoteData = useCallback((id: string, value: number): void => {
    _setVotedSongs((prev) => ({
      ...prev,
      [id]: value > 0
    }));
  }, []);

  const handleVote = useCallback(
    async (id: string, value: number): Promise<void> =>
      new Promise<void>((resolve) => {
        PlaylistService.updatevideovotesPartialUpdate(
          { id, votes: value },
          {
            headers: {
              Authorization: `Bearer ${userJwt}`
            }
          }
        )
          .then((res) => {
            if (res.status === 200) {
              updateVoteData(id, value);
            } else if (res.status === 204) {
              toast.success("Song deleted successfully.");
            } else {
              toast.error("Something went wrong, please try again.");
            }
          })
          .catch((res) => {
            if (res.response?.status === 400) {
              // TODO fix
              toast.error(
                res.response?.data.errors.votes?.[0] ??
                  "Something went wrong, please try again."
              );
            } else if (res.response?.status === 401) {
              dispatch(logout());
            } else {
              toast.error("Something went wrong, please try again.");
            }
          })
          .finally(() => {
            resolve();
          });
      }),
    [dispatch, updateVoteData, userJwt]
  );

  return (
    <Outlet
      context={{
        addItemToPlaylist,
        handleVote,
        upcomingSongs: _upcomingSongs
      }}
    />
  );
};
